'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo } from 'react';
import { useMount } from 'react-use';

import { FormattedMessage } from '@flipster/i18n';
import { Dialog } from '@flipster/react/design-system/dialog/index';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import { css } from '@flipster/styled-system/css';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { formatDatetime } from '@flipster/utils/datetime';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { MarginBonusEvent } from '@flipster/utils/segment/events/margin-bonus';

import { assetMarginBonusBunja } from '../../../../margin-bonus/state';
import { useDepositModal } from '../../../wallet-modal/deposit';
import { marginBonusExpirationDays, marginBonusExpirationPopupBunja } from './state';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

const ExpirationDialog = ({ isOpen, onClose, onExit }: Props) => {
  const openDepositModal = useDepositModal();
  const { shouldShowMarginBonusExpirationPopupAtom } = useBunja(marginBonusExpirationPopupBunja);
  const setShowMarginBonusExpirationPopup = useSetAtom(shouldShowMarginBonusExpirationPopupAtom);

  const { numberOfPositionsAtom, bonusExpirationStatusAtom, marginShortageAtom } = useBunja(assetMarginBonusBunja);
  const numberOfPositions = useAtomValue(numberOfPositionsAtom);
  const bonusStatus = useAtomValue(bonusExpirationStatusAtom);
  const marginShortage = useAtomValue(marginShortageAtom);

  const formattedExpiryDate = useMemo(
    () => `${formatDatetime(bonusStatus.expiry, { format: 'MMM dd, yyyy HH:mm', isUTC: true })} UTC`,
    [bonusStatus.expiry],
  );
  const amountExpiringInUsdt = Num(bonusStatus.amountExpiringInUsdt ?? 0);

  const trackingParams = {
    amount_expiring: amountExpiringInUsdt.toFixed(UsdtPrecision),
    expiry_date: formattedExpiryDate,
  };

  useMount(() => {
    segment.track(MarginBonusEvent.OrderFormBonusExpiryPopupView, trackingParams);
  });

  const handleClose = () => {
    setShowMarginBonusExpirationPopup('close');
    onClose();
  };

  return (
    <Dialog.Root isOpen={isOpen} onExit={onExit}>
      <Dialog.Title
        label={
          <FormattedMessage
            id='Assets.r251112.amount_symbol_bonus_expires_in_days_day'
            values={{
              amountSymbol: `${amountExpiringInUsdt.toFixed(0)} ${UsdtCurrency}`,
              days: marginBonusExpirationDays,
            }}
          />
        }
        leftAddon={<CautionCircleIcon size={24} color={token('colors.content.state.warning')} />}
      />
      <Dialog.Content>
        <div>
          <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5', mb: 4 })}>
            <FormattedMessage id='Assets.r251112.count_positions_impacted' values={{ count: numberOfPositions }} />
          </p>
          <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3', mb: 16 })}>
            <FormattedMessage id='Assets.r251112.At_expiry_bonus_margin_is_removed_info' />
          </p>
          {marginShortage.gt(0) && (
            <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
              <FormattedMessage
                id='Assets.r251112.Additional_funds_needed_colon_amount_symbol'
                values={{ amountSymbol: `${marginShortage.toFixed(UsdtPrecision)} ${UsdtCurrency}` }}
              />
            </p>
          )}
          <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
            <FormattedMessage id='Assets.r251112.Expiry_colon_date_time' values={{ dateTime: formattedExpiryDate }} />
          </p>
        </div>
      </Dialog.Content>
      <Dialog.Footer>
        <Dialog.Button
          hierarchy='secondary'
          color='neutral'
          label={<FormattedMessage id='Common.r250918.Got_it' />}
          onClick={handleClose}
        />
        <Dialog.Button
          hierarchy='primary'
          label={<FormattedMessage id='Assets.r250401.Deposit_now' />}
          onClick={() => {
            handleClose();
            segment.track(MarginBonusEvent.OrderFormBonusExpiryPopupDepositClick, trackingParams);
            openDepositModal();
          }}
        />
      </Dialog.Footer>
    </Dialog.Root>
  );
};

export default ExpirationDialog;
